import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import useFormValidation from 'hooks/useFormValidation';

import { adminOrderApi } from 'api';
import { STATE_LIST } from 'constants/address';

const validationSchema = {
  name: {
    required: 'Name is required',
  },
  email: {
    required: 'Email is required',
    type: 'email',
  },
  phone: {
    required: 'Phone is required',
    type: 'phone',
  },
  address_line_1: {
    required: 'Address Line 1 is required',
  },
  city: {
    required: 'City is required',
  },
  state: {
    required: 'State is required',
  },
  country: {
    required: 'Country is required',
  },
  pincode: {
    required: 'Pincode is required',
    type: 'number',
  },
};

const initialValues = {
  name: '',
  email: '',
  phone: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  country: '',
  pincode: '',
};

const AddressEditModal = ({
  show,
  onHide,
  onSuccess,
  type,
  title,
  orderId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    isValid,
    setValues,
  } = useFormValidation(initialValues, validationSchema);

  useEffect(() => {
    const fetchAddress = async () => {
      if (show && orderId) {
        setIsFetching(true);
        try {
          let response;
          if (type === 'billing') {
            response = await adminOrderApi.getBillingAddress(orderId);
          } else if (type === 'shipping') {
            response = await adminOrderApi.getShippingAddress(orderId);
          }

          if (response) {
            Object.keys(initialValues).forEach(field => {
              handleChange(field, response[field] || '');
            });
          }
        } catch (err) {
          toast.error('Failed to fetch address');
          onHide();
        } finally {
          setIsFetching(false);
        }
      }
    };

    fetchAddress();
  }, [show, orderId, type, setValues, onHide, handleChange]);

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (type === 'billing') {
        await adminOrderApi.updateBillingAddress(orderId, values);
      } else if (type === 'shipping') {
        await adminOrderApi.updateShippingAddress(orderId, values);
      }

      toast.success('Address updated successfully');
      onSuccess();
      closeModal();
    } catch (err) {
      toast.error('Failed to update address');
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    resetForm();
    onHide();
  };

  return (
    <Modal show={show} onHide={closeModal}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isFetching ? (
          <div className="d-flex justify-content-center align-items-center p-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>
                Name<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={values.name}
                onChange={e => handleChange('name', e.target.value)}
                onBlur={() => handleBlur('name')}
                isInvalid={touched.name && errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>
                Email<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                value={values.email}
                onChange={e => handleChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                isInvalid={touched.email && errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>
                Phone<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={values.phone}
                onChange={e => handleChange('phone', e.target.value)}
                onBlur={() => handleBlur('phone')}
                isInvalid={touched.phone && errors.phone}
              />
              <Form.Control.Feedback type="invalid">
                {errors.phone}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>
                Address Line 1<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={values.address_line_1}
                onChange={e => handleChange('address_line_1', e.target.value)}
                onBlur={() => handleBlur('address_line_1')}
                isInvalid={touched.address_line_1 && errors.address_line_1}
              />
              <Form.Control.Feedback type="invalid">
                {errors.address_line_1}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control
                type="text"
                value={values.address_line_2}
                onChange={e => handleChange('address_line_2', e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>
                City<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={values.city}
                onChange={e => handleChange('city', e.target.value)}
                onBlur={() => handleBlur('city')}
                isInvalid={touched.city && errors.city}
              />
              <Form.Control.Feedback type="invalid">
                {errors.city}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>
                State<span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={values.state}
                onChange={e => handleChange('state', e.target.value)}
                onBlur={() => handleBlur('state')}
                isInvalid={touched.state && errors.state}
              >
                <option value="">Select State...</option>
                {STATE_LIST.map(state => (
                  <option key={state.value} value={state.value}>
                    {state.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.state}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>
                Country<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={values.country}
                onChange={e => handleChange('country', e.target.value)}
                onBlur={() => handleBlur('country')}
                isInvalid={touched.country && errors.country}
              />
              <Form.Control.Feedback type="invalid">
                {errors.country}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>
                Pincode<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={values.pincode}
                onChange={e => handleChange('pincode', e.target.value)}
                onBlur={() => handleBlur('pincode')}
                isInvalid={touched.pincode && errors.pincode}
              />
              <Form.Control.Feedback type="invalid">
                {errors.pincode}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isValid || isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddressEditModal;
